import { ReactNode } from 'react';

import {
  UseComboboxGetItemPropsOptions,
  UseMultipleSelectionGetSelectedItemPropsOptions,
} from 'downshift';

import { DefaultItem, GenericItem, GetSelectedItemProps } from '../types';

const getDisabledState = <TItem extends GenericItem>({
  disabledArg,
  item,
  index,
  isItemDisabled,
}: {
  disabledArg?: boolean;
  item: TItem;
  index?: number;
  isItemDisabled: (item: TItem, index: number) => boolean;
}) => {
  if (disabledArg !== undefined) {
    return disabledArg;
  }

  if (item.disabled !== undefined) {
    return !!item.disabled;
  }

  if (index !== undefined) {
    return isItemDisabled(item, index);
  }

  return false;
};

/**
 * Wrapper for getItemProps which adds the "disabled" and "isHighlighted" properties on the given item.
 * If the item's "disabled" prop is provided it will set the "disabled" for getItemProps.
 * This prevents selection of disabled items.
 * @param args.getItemProps prop getter returned from downshift hook
 * @param args.isItemDisabled function to determine whether an item is disabled
 * @param args.highlightedIndex prop returned from downshift hook
 * @returns modified version of getItemProps
 */
export const wrapGetItemProps =
  <TItem extends GenericItem>({
    getItemProps,
    isItemDisabled,
    highlightedIndex,
    selectedItem,
    indexGetter,
  }: {
    getItemProps: (
      options: UseComboboxGetItemPropsOptions<TItem> & {
        isHighlighted: boolean;
        text?: ReactNode;
        subtext?: ReactNode;
      },
      // any comes from downshift typings
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ) => any;
    isItemDisabled: (item: TItem, index: number) => boolean;
    highlightedIndex: number;
    selectedItem: TItem | null;
    indexGetter: null | ((item: TItem) => number | undefined);
  }) =>
  ({
    item,
    index: indexProp,
    disabled,
    isSelected: isSelectedProp,
    ...rest
  }: UseComboboxGetItemPropsOptions<TItem>) => {
    const index = indexProp !== undefined ? indexProp : indexGetter?.(item);

    return getItemProps({
      item,
      index,
      disabled: getDisabledState({
        disabledArg: disabled,
        item,
        index,
        isItemDisabled,
      }),
      isHighlighted: index === highlightedIndex,
      isSelected:
        isSelectedProp !== undefined ? isSelectedProp : selectedItem === item,
      text: (item as unknown as DefaultItem).label,
      subtext: (item as unknown as DefaultItem).subtext,
      ...rest,
    });
  };

/**
 * Used by internal Dropdown hooks to handle the return value of useDisabledItems.
 * Wrapper for getItemProps which checks for the "disabled" property on the given item.
 * This property is added by the useDisabledItems hook.
 * If the item's "disabled" prop is provided it will set the "disabled" for getItemProps.
 * This prevents selection of disabled items.
 * @param getSelectedItemProps prop getter returned from downshift hook
 * @returns modified version of getItemProps
 */
export const wrapGetSelectedItemProps =
  <TItem extends GenericItem>({
    getSelectedItemProps,
    isItemDisabled,
  }: {
    getSelectedItemProps: (
      options: UseMultipleSelectionGetSelectedItemPropsOptions<TItem>,
      // any comes from downshift typings
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ) => any;
    isItemDisabled: (item: TItem, index: number) => boolean;
  }): GetSelectedItemProps<TItem> =>
  ({ selectedItem, disabled, index, ...rest }) =>
    getSelectedItemProps({
      selectedItem,
      index,
      disabled: getDisabledState({
        disabledArg: disabled,
        item: selectedItem,
        index,
        isItemDisabled,
      }),
      ...rest,
    });
